import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Logo from './components/Logo';
import Login from './components/Login';
import Calendar from './components/Calendar';
import AdminPanel from './components/AdminPanel';
import CustomerReport from './components/CustomerReport';
import PaymentSuccess from './components/PaymentSuccess'; // Import the new component
import TenantPortal from './components/TenantPortal';
import ResetPassword from './components/ResetPassword';
import RequestAccess from './components/RequestAccess';
import RequestAccessStatus from './components/RequestAccessStatus';
import BusinessSplash from './components/BusinessSplash';
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';


function parseJwt(token) {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch (e) {
    return null;
  }
}

function App() {
  const [token, setToken] = useState(() => localStorage.getItem('calphender_token'));
  const [userRole, setUserRole] = useState(null);
  const [userTenantId, setUserTenantId] = useState('');
  const [supportMode, setSupportMode] = useState(false);
  const [companyInfo, setCompanyInfo] = useState({ name: 'CalTronic.Pro' });
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [customerContact, setCustomerContact] = useState({
    fullName: '',
    email: '',
    phone: '',
    message: '',
  });
  const [versionInfo, setVersionInfo] = useState(null);
  const [contactSaveMessage, setContactSaveMessage] = useState('');
  const runtimeEnv = String(versionInfo?.env || '').toLowerCase();
  const isLocalHost = typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname);
  const isDevelopment = runtimeEnv ? runtimeEnv !== 'production' : isLocalHost;
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
  const showPublicContactCard = !token && ['/', '/business', '/about'].includes(currentPath);

  const handleSetToken = (newToken) => {
    if (newToken) {
      localStorage.setItem('calphender_token', newToken);
    } else {
      localStorage.removeItem('calphender_token');
    }
    setToken(newToken);
  };

  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        const response = await fetch('/api/company-info');
        if (response.ok) {
          const data = await response.json();
          setCompanyInfo(data);
        } else {
          console.error('Failed to fetch company info. Status:', response.status);
        }
      } catch (error) {
        console.error('Error fetching company info:', error);
      }
    };

    fetchCompanyInfo();
  }, []);

  useEffect(() => {
    const fetchVersionInfo = async () => {
      try {
        const response = await fetch('/api/version');
        if (!response.ok) return;
        const data = await response.json();
        setVersionInfo(data);
      } catch (_error) {
        // Ignore version indicator errors to avoid impacting app usage
      }
    };

    fetchVersionInfo();
  }, []);

  useEffect(() => {
    if (token) {
      const payload = parseJwt(token);
      setUserRole(payload?.role || 'user');
      setUserTenantId(String(payload?.tenantId || ''));
      setSupportMode(Boolean(payload?.supportMode));
    } else {
      setUserRole(null);
      setUserTenantId('');
      setSupportMode(false);
    }
  }, [token]);

  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      response => response,
      error => {
        const status = error?.response?.status;
        const apiError = String(error?.response?.data?.error || '').toLowerCase();
        const requestUrl = String(error?.config?.url || '').toLowerCase();
        const isAuthEndpoint = requestUrl.includes('/auth/login') || requestUrl.includes('/auth/forgot-password') || requestUrl.includes('/auth/reset-password');
        const isExpiredSession = status === 401 || apiError.includes('invalid token') || apiError.includes('missing authorization') || apiError.includes('invalid authorization');

        if (!isAuthEndpoint && isExpiredSession) {
          handleSetToken(null);
          if (window.location.pathname !== '/login') {
            alert('Your session expired. Please log in again.');
            window.location.href = '/login';
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('calphender_admin_token');
    handleSetToken(null);
  };

  const handleStartSupportSession = (supportToken) => {
    const nextToken = String(supportToken || '').trim();
    if (!nextToken) return;
    if (token) {
      localStorage.setItem('calphender_admin_token', token);
    }
    handleSetToken(nextToken);
    window.location.href = '/';
  };

  const handleReturnToAdmin = () => {
    const adminToken = String(localStorage.getItem('calphender_admin_token') || '').trim();
    if (!adminToken) {
      handleLogout();
      return;
    }
    localStorage.removeItem('calphender_admin_token');
    handleSetToken(adminToken);
    window.location.href = '/admin';
  };

  const isAdmin = userRole === 'admin';
  const canMaintainTenant = userRole === 'office' && Boolean(String(userTenantId || '').trim());
  const roleButton = canMaintainTenant
    ? { to: '/tenant-portal', label: 'Tenant' }
    : (isAdmin ? { to: '/admin', label: 'Admin' } : null);
  const publicEmail = String(companyInfo?.email || '').trim();
  const publicPhone = String(companyInfo?.phone || '').trim();
  const publicAddress = String(companyInfo?.address || '').trim();
  const contactFormToEmail = 'contact@caltronic.pro';

  const handleCustomerFieldChange = (field, value) => {
    setCustomerContact((prev) => ({ ...prev, [field]: value }));
  };

  const openCustomerMailtoDraft = () => {
    setContactSaveMessage('');

    const to = contactFormToEmail;
    if (!to) {
      setContactSaveMessage('Contact email is not available right now. Please call the listed phone number.');
      return;
    }

    const name = String(customerContact.fullName || '').trim();
    const email = String(customerContact.email || '').trim();
    const phone = String(customerContact.phone || '').trim();
    const message = String(customerContact.message || '').trim();

    const subject = name
      ? `Customer contact request from ${name}`
      : 'Customer contact request';
    const body = [
      `Name: ${name || '(not provided)'}`,
      `Email: ${email || '(not provided)'}`,
      `Phone: ${phone || '(not provided)'}`,
      '',
      'Message:',
      message || '(not provided)'
    ].join('\n');

    const mailtoHref = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoHref;
    setContactSaveMessage('Your email app should open with this information prefilled.');
  };

  return (
    <Router>
      <div className={`App${isDevelopment ? ' App--dev' : ''}`}>
        <header className="App-header">
          <Logo />
          {!token && (
            <div className="public-header-info-wrap">
              <div className="public-header-contact" aria-label="Public contact details">
                <span className="public-header-contact-row"><strong className="public-header-contact-label">Email:</strong><span className="public-header-contact-value">{publicEmail ? <a href={`mailto:${publicEmail}`}>{publicEmail}</a> : 'Not published'}</span></span>
                <span className="public-header-contact-row"><strong className="public-header-contact-label">Phone:</strong><span className="public-header-contact-value">{publicPhone || 'Not published'}</span></span>
                <span className="public-header-contact-row"><strong className="public-header-contact-label">Address:</strong><span className="public-header-contact-value">{publicAddress || 'Not published'}</span></span>
              </div>
              {showPublicContactCard && (
                <button type="button" className="contact-card-link" onClick={() => setIsContactModalOpen(true)}>
                  Open Contact Card
                </button>
              )}
            </div>
          )}
          {token && (
            <div className="app-header-auth">
              <div className="header-button-frame">
                <nav className="App-nav App-nav-grid">
                  <Link to="/" className="nav-button">Calendar</Link>
                  <Link to="/reports" className="nav-button">Reports</Link>
                  {roleButton ? (
                    <Link to={roleButton.to} className="nav-button">{roleButton.label}</Link>
                  ) : (
                    <span className="nav-button-spacer" aria-hidden="true" />
                  )}
                </nav>
                <div className="header-action-grid">
                  {supportMode && (
                    <button onClick={handleReturnToAdmin} className="logout-button support-return-button">
                      Return to Admin
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
          {(token || versionInfo) && (
            <div className="header-meta-stack">
              {token && (
                <button onClick={handleLogout} className="logout-button logout-button--meta">
                  Logout
                </button>
              )}
              {versionInfo && (
                <div className="live-indicator header-meta-chip" title="Live code indicator">
                  <span>{`v${versionInfo.appVersion || 'unknown'}`}</span>
                  <span>{versionInfo.commit ? `@${versionInfo.commit}` : ''}</span>
                </div>
              )}
            </div>
          )}
        </header>
        {isContactModalOpen && (
          <div className="contact-modal-overlay" role="dialog" aria-modal="true" aria-label="Customer contact card" onClick={() => setIsContactModalOpen(false)}>
            <div className="contact-modal" onClick={(event) => event.stopPropagation()}>
              <div className="contact-modal-header-row">
                <h2>Customer Contact Information</h2>
                <button type="button" className="contact-modal-close" onClick={() => setIsContactModalOpen(false)} aria-label="Close contact card">
                  x
                </button>
              </div>
              <p className="contact-modal-subtitle">Use this simple form to email your contact details to {contactFormToEmail}.</p>

              <div className="contact-form-grid">
                <label className="contact-form-span-2"><span>Your Name</span><input type="text" value={customerContact.fullName} onChange={(e) => handleCustomerFieldChange('fullName', e.target.value)} /></label>
                <label><span>Email</span><input type="email" value={customerContact.email} onChange={(e) => handleCustomerFieldChange('email', e.target.value)} /></label>
                <label><span>Phone</span><input type="text" value={customerContact.phone} onChange={(e) => handleCustomerFieldChange('phone', e.target.value)} /></label>
                <label className="contact-form-span-2"><span>Message</span><textarea rows="4" value={customerContact.message} onChange={(e) => handleCustomerFieldChange('message', e.target.value)} /></label>
              </div>

              <div className="contact-transfer-actions contact-transfer-actions--single">
                <button type="button" className="contact-action-button" onClick={openCustomerMailtoDraft}>Email {contactFormToEmail}</button>
              </div>
              {contactSaveMessage && <p className="contact-modal-save-message">{contactSaveMessage}</p>}
            </div>
          </div>
        )}
        <main>
          <Routes>
            <Route path="/business" element={<BusinessSplash companyInfo={companyInfo} />} />
            <Route path="/about" element={<BusinessSplash companyInfo={companyInfo} />} />
            <Route path="/payment_success" element={<PaymentSuccess />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/login" element={!token ? <Login setToken={handleSetToken} /> : <Navigate to="/" />} />
            <Route path="/request-access" element={!token ? <RequestAccess /> : <Navigate to="/" />} />
            <Route path="/request-access/status" element={!token ? <RequestAccessStatus /> : <Navigate to="/" />} />
            <Route path="/" element={token ? <Calendar token={token} onLogout={handleLogout} /> : <BusinessSplash companyInfo={companyInfo} />} />
            <Route path="/tenant-portal" element={token && canMaintainTenant ? <TenantPortal token={token} /> : <Navigate to="/login" />} />
            <Route path="/admin" element={token && userRole === 'admin' ? <AdminPanel token={token} onStartSupportSession={handleStartSupportSession} /> : <Navigate to="/login" />} />
            <Route path="/reports" element={token ? <CustomerReport token={token} /> : <Navigate to="/login" />} />
            <Route path="/vehicle-maintenance" element={<Navigate to="/" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </Router>
  );
}

export default App;